"use client"

import { useState } from "react"
import { Send, CheckCircle, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { fetchWithTrace } from "@/lib/fetch-with-trace"
import { Logger } from "@/lib/logger-client"

interface ChatbotConnectFormProps {
  onSubmitted: (name: string) => void
}

export default function ChatbotConnectForm({ onSubmitted }: ChatbotConnectFormProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [errorMsg, setErrorMsg] = useState("")

  const canSubmit = name.trim() && email.trim() && query.trim() && status === "idle"

  const handleSubmit = async () => {
    if (!canSubmit) return
    setStatus("sending")
    setErrorMsg("")

    try {
      const { response: res } = await fetchWithTrace("/api/chatbot/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          query: query.trim(),
        }),
        logger: Logger(),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Failed to send")
      }

      setStatus("sent")
      onSubmitted(name.trim())
    } catch (err: any) {
      setStatus("error")
      setErrorMsg(err.message || "Something went wrong. Please try again.")
    }
  }

  if (status === "sent") {
    return (
      <div className="flex items-start gap-2 rounded-xl bg-muted p-3">
        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
        <div className="text-sm">
          <p className="font-medium">We've received your details!</p>
          <p className="text-muted-foreground">
            Our team will reach out to you within 24 hours. Check your email for a confirmation.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-muted/50 p-3 space-y-2.5">
      <p className="text-sm font-medium">Connect with our team</p>
      <input
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        data-interactive="true"
      />
      <input
        type="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        data-interactive="true"
      />
      <textarea
        placeholder="Your query"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        rows={2}
        className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        data-interactive="true"
      />
      {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className={cn(
          "inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          canSubmit
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-muted text-muted-foreground cursor-not-allowed"
        )}
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending...
          </>
        ) : (
          <>
            <Send className="h-3.5 w-3.5" /> Submit
          </>
        )}
      </button>
    </div>
  )
}
